package screens.admin

import androidx.compose.runtime.*
import com.example.readingnotes.di.AppModule
import com.example.readingnotes.presentation.stripeconnect.StripeConnectEvent
import com.example.readingnotes.presentation.stripeconnect.StripeConnectViewModel
import kotlinx.browser.window
import kotlinx.coroutines.CoroutineScope
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import styles.AppStyleSheet

// Helper function for lowercase conversion in Kotlin/JS
private fun String.lowercaseStr(): String {
    @Suppress("UnsafeDynamicallyTypedCall")
    return js("this.toLowerCase()") as String
}

@Composable
fun StripeConnectScreen(
    onBackClick: () -> Unit,
    appModule: AppModule,
    appScope: CoroutineScope,
) {
    val viewModel =
        remember {
            StripeConnectViewModel(
                getStripeConnectStatusUseCase = appModule.getStripeConnectStatusUseCase,
                getStripeConnectOnboardingLinkUseCase = appModule.getStripeConnectOnboardingLinkUseCase,
                handleStripeConnectCallbackUseCase = appModule.handleStripeConnectCallbackUseCase,
                refreshStripeConnectStatusUseCase = appModule.refreshStripeConnectStatusUseCase,
                viewModelScope = appScope,
            )
        }
    val state by viewModel.state.collectAsState()

    // Check for OAuth callback in URL
    LaunchedEffect(Unit) {
        @Suppress("UnsafeDynamicallyTypedCall")
        val urlParams = js("new URLSearchParams(window.location.search)")
        @Suppress("UnsafeDynamicallyTypedCall")
        val code = urlParams.get("code") as? String
        if (code != null) {
            viewModel.onEvent(StripeConnectEvent.OnCallback(code))
            // Clear the code from URL
            window.history.replaceState(null, "", "/settings/stripe-connect")
        }
    }

    // Container div with proper styling
    Div({
        classes(AppStyleSheet.container)
        style {
            property("max-width", "800px")
            property("margin", "0 auto")
            padding(20.px)
        }
    }) {
        // Header with back button
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                marginBottom(24.px)
            }
        }) {
            Button({
                classes(AppStyleSheet.backButton)
                onClick { onBackClick() }
            }) { Text("← 戻る") }

            H2({
                style {
                    margin(0.px)
                    marginLeft(16.px)
                }
            }) {
                Text("収益化設定")
            }
        }

        // Main content
        val accountStatus = state.accountStatus
        when {
            state.isLoading -> {
                Div({
                    classes(AppStyleSheet.loading)
                    attr("role", "status")
                    attr("aria-live", "polite")
                    attr("aria-label", "読み込み中")
                }) {
                    Div({ classes(AppStyleSheet.loadingSpinner) }) {}
                    Text("読み込み中...")
                }
            }

            state.errorMessage != null -> {
                Div({
                    classes(AppStyleSheet.formError)
                    style {
                        marginBottom(16.px)
                    }
                }) {
                    Text(state.errorMessage ?: "エラーが発生しました")
                }
                Button({
                    classes(AppStyleSheet.primaryButton)
                    onClick {
                        viewModel.onEvent(StripeConnectEvent.OnClearError)
                        viewModel.onEvent(StripeConnectEvent.OnRefresh)
                    }
                }) {
                    Text("再試行")
                }
            }

            accountStatus == null || !accountStatus.connected -> {
                NotConnectedContent(
                    onStartOnboarding = {
                        viewModel.onEvent(StripeConnectEvent.OnStartOnboarding)
                    },
                    isGenerating = state.isGeneratingOnboardingLink,
                )
            }

            else -> {
                if (accountStatus.onboardingCompleted) {
                    ConnectedContent(
                        status = accountStatus,
                        isRefreshing = state.isRefreshing,
                        onRefresh = { viewModel.onEvent(StripeConnectEvent.OnRefresh) },
                        onOpenDashboard = { viewModel.onEvent(StripeConnectEvent.OnOpenDashboard) },
                    )
                } else if (accountStatus != null && accountStatus.detailsSubmitted) {
                    InProgressContent(
                        onStartOnboarding = {
                            viewModel.onEvent(StripeConnectEvent.OnStartOnboarding)
                        },
                        isGenerating = state.isGeneratingOnboardingLink,
                    )
                } else {
                NotConnectedContent(
                    onStartOnboarding = {
                        viewModel.onEvent(StripeConnectEvent.OnStartOnboarding)
                    },
                    isGenerating = state.isGeneratingOnboardingLink,
                )
                }
            }
        }
    }
}

@Composable
private fun NotConnectedContent(
    onStartOnboarding: () -> Unit,
    isGenerating: Boolean,
) {
    Div({
        style {
            property("background-color", "#ffffff")
            property("border-radius", "8px")
            property("padding", "24px")
            property("box-shadow", "0 1px 3px rgba(0,0,0,0.1)")
        }
    }) {
        H3({
            style {
                property("margin", "0 0 16px 0")
                property("font-size", "20px")
            }
        }) {
            Text("有料記事を販売するには、Stripe Connectアカウントが必要です。")
        }

        P({
            style {
                property("margin", "0 0 8px 0")
                property("color", "#666")
            }
        }) {
            Text("Stripeは安全な決済プラットフォームです。")
        }

        P({
            style {
                property("margin", "0 0 24px 0")
                property("color", "#666")
            }
        }) {
            Text("販売した収益は、Stripeを通じてあなたの銀行口座へ出金されます。")
        }

        Button({
            classes(AppStyleSheet.primaryButton)
            onClick { onStartOnboarding() }
            if (isGenerating) {
                attr("disabled", "true")
            }
            style {
                property("width", "100%")
            }
        }) {
            Text(if (isGenerating) "処理中..." else "Stripe Connect連携を開始")
        }

        Div({
            style {
                property("margin-top", "24px")
                property("padding-top", "16px")
                property("border-top", "1px solid #e5e7eb")
            }
        }) {
            P({
                style {
                    property("margin", "0 0 8px 0")
                    property("font-weight", "500")
                }
            }) {
                Text("機能:")
            }
            Ul({
                style {
                    property("margin", "0")
                    property("padding-left", "20px")
                }
            }) {
                Li { Text("安全な決済処理") }
                Li { Text("週次での出金") }
                Li { Text("詳細な売上レポート") }
            }
        }
    }
}

@Composable
private fun InProgressContent(
    onStartOnboarding: () -> Unit,
    isGenerating: Boolean,
) {
    Div({
        style {
            property("background-color", "#ffffff")
            property("border-radius", "8px")
            property("padding", "24px")
            property("box-shadow", "0 1px 3px rgba(0,0,0,0.1)")
        }
    }) {
        H3({
            style {
                property("margin", "0 0 16px 0")
                property("font-size", "20px")
                property("color", "#F59E0B")
            }
        }) {
            Text("情報入力中...")
        }

        P({
            style {
                property("margin", "0 0 24px 0")
                property("color", "#666")
            }
        }) {
            Text("Stripeアカウントの設定がまだ完了していません。")
        }

        Button({
            classes(AppStyleSheet.secondaryButton)
            onClick { onStartOnboarding() }
            if (isGenerating) {
                attr("disabled", "true")
            }
            style {
                property("width", "100%")
            }
        }) {
            Text(if (isGenerating) "処理中..." else "再開")
        }
    }
}

@Composable
private fun ConnectedContent(
    status: com.example.readingnotes.domain.model.StripeConnectStatus,
    isRefreshing: Boolean,
    onRefresh: () -> Unit,
    onOpenDashboard: () -> Unit,
) {
    Div({
        style {
            property("background-color", "#ffffff")
            property("border-radius", "8px")
            property("padding", "24px")
            property("box-shadow", "0 1px 3px rgba(0,0,0,0.1)")
        }
    }) {
        H3({
            style {
                property("margin", "0 0 16px 0")
                property("font-size", "20px")
                property("color", "#10B981")
            }
        }) {
            Text("連携完了")
        }

        H4({
            style {
                property("margin", "0 0 16px 0")
                property("font-size", "16px")
            }
        }) {
            Text("Stripe Connect アカウント情報")
        }

        Div({
            style {
                property("margin-bottom", "24px")
            }
        }) {
            Div({ style { property("margin-bottom", "8px") } }) {
                Text("• 国: ${status.country ?: "N/A"}")
            }
            Div({ style { property("margin-bottom", "8px") } }) {
                Span({
                    style {
                        property("color", if (status.chargesEnabled) "#10B981" else "#6B7280")
                    }
                }) {
                    Text("• 支払い受領: ${if (status.chargesEnabled) "有効" else "無効"}")
                }
            }
            Div({ style { property("margin-bottom", "8px") } }) {
                Span({
                    style {
                        property("color", if (status.payoutsEnabled) "#10B981" else "#6B7280")
                    }
                }) {
                    Text("• 出金: ${if (status.payoutsEnabled) "有効" else "無効"}")
                }
            }
            Div({ style { property("margin-bottom", "8px") } }) {
                Span({
                    style {
                        property("color", if (status.detailsSubmitted) "#10B981" else "#F59E0B")
                    }
                }) {
                    Text("• 詳細登録: ${if (status.detailsSubmitted) "完了" else "未完了"}")
                }
            }
        }

        Button({
            classes(AppStyleSheet.secondaryButton)
            onClick { onRefresh() }
            if (isRefreshing) {
                attr("disabled", "true")
            }
            style {
                property("width", "100%")
                property("margin-bottom", "16px")
            }
        }) {
            Text(if (isRefreshing) "更新中..." else "更新")
        }

        P({
            style {
                property("margin", "0 0 16px 0")
                property("color", "#666")
                property("text-align", "center")
                property("font-size", "14px")
            }
        }) {
            Text("Stripe Connectを通じて受け取った収益は、")
            Br()
            Text("登録された銀行口座に出金されます。")
        }

        A(
            href = "https://dashboard.stripe.com/${status.country?.lowercaseStr() ?: "test"}",
            attrs = {
                attr("target", "_blank")
                attr("rel", "noopener noreferrer")
                classes(AppStyleSheet.linkStyle)
                style {
                    property("display", "block")
                    property("text-align", "center")
                    property("font-weight", "500")
                }
                onClick { onOpenDashboard() }
            }
        ) {
            Text("Stripeアカウント管理")
        }
    }
}
